import { components, paths } from "@api/api";
import useAxios from "@hooks/useFetch";
import { useMemo } from "react";
import { KeywordBase, KeywordTableColumn, useGetKeywordBaseData } from "@api/useGetKeywordBaseData";
import { AmongType, getAmongTypeKey } from "@models/Among";
import { getFormattedThousand } from "@datassential/platform-ui-lib";
import { Metric } from "@models/Metric";

const REQUEST_PATH: keyof paths = '/api/Search/GetBrands';
export type HomeBrandsRawNormRequestPayload = components["schemas"]["FlavorTracker.Entities.DTO.SearchPage.SearchPageMetricRequest"];
type ResponseData = components["schemas"]["FlavorTracker.Entities.DTO.SearchPage.SearchPageBrandsResponse"][];

export type HomeBrandsRawNormDataItem = KeywordBase & {
    columns: Map<string, KeywordTableColumn<number>>;
}

export function useHomeBrandsRawNormData(payload: HomeBrandsRawNormRequestPayload) {
    const getKeywordBaseData = useGetKeywordBaseData();

    const response = useAxios<ResponseData, HomeBrandsRawNormRequestPayload>({
        url: REQUEST_PATH,
        method: 'post',
        payload,
    });

    const isBaseSizeView = useMemo(
        () => payload.metric === null,
        [payload.metric]
    );

    const isPolarityView = useMemo(
        () => payload.metric === Metric.PolarityScore,
        [payload.metric]
    );

    const data = useMemo(
        (): HomeBrandsRawNormDataItem[] => {
            return (response.data ?? []).map(item => {
                const itemBase = getKeywordBaseData(item.id ?? 0);
                const columns = new Map<string, KeywordTableColumn<number>>();

                const linearData = [
                    ...(item.dataByRespondentType ?? []).map(i => ({ ...i, type: AmongType.RespondentType })),
                    ...(item.dataByChain ?? []).map(i => ({ ...i, type: AmongType.Chain })),
                    ...(item.dataBySegment ?? []).map(i => ({ ...i, type: AmongType.Segment })),
                    ...(item.dataByCpgBrand ?? []).map(i => ({ ...i, type: AmongType.Brand })),
                    ...(item.dataByOnSite ?? []).map(i => ({ ...i, type: AmongType.OnSite })),
                    ...(item.dataByDeliveryService ?? []).map(i => ({ ...i, type: AmongType.Delivery })),
                ];

                linearData.forEach((i) => {
                    const key = getAmongTypeKey(i.id ?? 0, i.type);
                    if (isBaseSizeView) {
                        columns.set(key, {
                            id: i.id ?? 0,
                            name: '',
                            value: i.base ?? null,
                            tableValue: getFormattedThousand(i.base),
                            copyTableValue: getFormattedThousand(i.base),
                            sortValue: i.base ?? null,
                            norm: 0,
                        });
                    } else if (isPolarityView) {
                        const value = i.norm ?? null;

                        columns.set(key, {
                            id: i.id ?? 0,
                            name: '',
                            value,
                            tableValue: value !== null ? getFormattedThousand(value) : '-',
                            copyTableValue: value !== null ? `${value}` : '-',
                            sortValue: value,
                            norm: i.norm ?? 0,
                        });
                    } else {
                        const value = i.raw ?? null;

                        columns.set(key, {
                            id: i.id ?? 0,
                            name: '',
                            value,
                            tableValue: value !== null ? `${value}%` : '-',
                            copyTableValue: value !== null ? `${value}%` : '-',
                            sortValue: i.originalRaw ?? null,
                            norm: i.norm ?? 0,
                        });
                    }

                });

                return {
                    ...itemBase,
                    columns,
                };
            });
        },
        [response.data, getKeywordBaseData, isBaseSizeView, isPolarityView]
    );

    return {
        ...response,
        data,
    };
}